import React, { JSXElementConstructor, PureComponent } from 'react';
import { Action, bindActionCreators, Dispatch } from 'redux';
import { connect } from 'react-redux';
import queryString from 'qs';
import UnauthedHeader from 'components/UnauthedHeader';
import { actions as accountActions } from 'reducers/Account';
import {
  accountErrorSelector,
  accountSelector,
  isUpdatingAccountSelector,
} from 'selectors/Account';
import { Card } from 'Common/components/Atoms/Card';
import { Div } from 'Common/components/Atoms/Div';
import { Flex } from 'Common/components/Atoms/Flex';
import { FontIcon } from 'Common/components/Atoms/FontIcon';
import { Link } from 'Common/components/Atoms/Link';
import { Loading } from 'Common/components/Atoms/Loading';
import { Toast } from 'Common/components/Molecules/Toast';
import { Text } from 'Common/components/Atoms/Text';
import { faEnvelope } from '@fortawesome/pro-regular-svg-icons';
import { Theme } from '@mui/material/styles';
import { withTheme } from '@mui/styles';
import { Email } from 'constants/email';
import { mail } from 'fluent-mailto';
import { HttpError } from 'sagas/Clients/HttpError';

interface Props {
  userName?: string;
  email?: string;
  error?: string | HttpError;
  isUpdating: boolean;
  isLoggedIn: boolean;
  isVerificationFailed: boolean;
}

interface OwnProps {
  theme: Theme;
  location: any;
}

interface DispatchProps {
  fetchAccount: () => void;
  resendEmail: () => void;
}

type AllProps = Props & OwnProps & DispatchProps;

interface State {
  showToast: boolean;
}

class EmailVerificationStep extends PureComponent<AllProps, State> {
  state = { showToast: false };

  componentDidMount() {
    const { fetchAccount } = this.props;
    /**
     * Call fetchAccount in case someone is reloading this email verify page. If someone is now logged in
     * and verified, then this will automatically redirect to the dashboard.
     */
    fetchAccount();
  }

  componentDidUpdate(prevProps: AllProps) {
    if (!this.props.isUpdating && prevProps.isUpdating && !this.props.error) {
      this.setState({ showToast: true });
    }
  }

  private submit = (e: React.MouseEvent<HTMLElement, MouseEvent>) => {
    e.preventDefault();
    const { isUpdating } = this.props;
    if (isUpdating) {
      return;
    }

    this.props.resendEmail();
  };

  render = () => {
    const { isLoggedIn, isUpdating, isVerificationFailed, email, theme } =
      this.props;
    if (!isLoggedIn) {
      return null;
    }
    const error = isVerificationFailed ? 'Verification failed' : null;

    return (
      <Div>
        <UnauthedHeader {...this.props} />
        <Text variant="h1" textAlign="center" mt="5rem" mb="2rem">
          Verify your email
        </Text>
        <Card display="block" width={480} m="auto" px={6} py={4}>
          <Text variant="body1" mb={4}>
            Thanks for signing up!
          </Text>
          <Text variant="body1">
            We just sent you a verification email to your address below:
          </Text>
          <Flex justifyContent="center">
            <Flex my={3} alignItems="center">
              <Text
                variant="h3"
                fontWeight={theme.typography.fontWeightRegular}
              >
                <FontIcon icon={faEnvelope} size="1x" mr={2} />
                <b>{email}</b>.
              </Text>
            </Flex>
          </Flex>
          <Text variant="body1" mb={3}>
            Please open the email and click the verification link to continue.
          </Text>
          <Text variant="body1">
            Didn't receive an email?{' '}
            <Link onClick={this.submit}>Click here</Link> to resend it.
          </Text>
          <Text variant="body1">
            Unable to verify your email? Please contact{' '}
            <Link href={mail.to(Email.SUPPORT).build()}>{Email.SUPPORT}</Link>
          </Text>
        </Card>
        {isUpdating && (
          <Flex alignItems="center" justifyContent="center" direction="column">
            <Text variant="body1" color="secondary" mt={3} textAlign="center">
              Resending Email
            </Text>
            <Loading size={64} color="secondary" />
          </Flex>
        )}
        {error && (
          <Text variant="body1" color="error" my={3} textAlign="center">
            {error}
          </Text>
        )}
        <Toast
          severity="success"
          showToast={this.state.showToast}
          handleClose={() => this.setState({ showToast: false })}
          message={`We just re-sent your verification email to ${email}.`}
          horizontal="center"
          vertical="bottom"
          icon={faEnvelope}
        />
      </Div>
    );
  };
}

function mapStateToProps(state: any, ownProps: OwnProps) {
  const account = accountSelector(state);
  const { user } = account;
  const isUpdating = isUpdatingAccountSelector(state);
  const error = accountErrorSelector(state);

  return {
    isUpdating,
    userName: user?.name,
    isLoggedIn: !!user,
    email: user?.email,
    error,
    isVerificationFailed: Boolean(
      queryString.parse(ownProps.location.search).failed
    ),
  };
}

function mapDispatchToProps(dispatch: Dispatch<Action>): DispatchProps {
  return bindActionCreators({ ...accountActions }, dispatch);
}

export default withTheme<Theme, React.JSXElementConstructor<AllProps>>(
  connect(mapStateToProps, mapDispatchToProps)(EmailVerificationStep)
);
